import { useState } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';
import PasswordInput from '../components/PasswordInput';
import { getPasswordPolicyError, PASSWORD_POLICY_MESSAGE } from '../utils/passwordPolicy';
import { buildMarketingSignupField, getMarketingVisitorId } from '../utils/marketingAttribution';

// API base URL - configurable via environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const REAUTH_RETURN_PATH_KEY = 'black_peak_reauth_return_path';

function Login({ setIsAuthenticated, isAuthenticated }) {
  const location = useLocation();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordPolicyError, setPasswordPolicyError] = useState('');
  const [error, setError] = useState('');
  const [authStage, setAuthStage] = useState('credentials');
  const [challengeToken, setChallengeToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaMessage, setMfaMessage] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const navigate = useNavigate();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const associateMarketingVisitorAfterRegistration = async () => {
    if (!location.state?.marketingRegistration) return;
    const visitorId = getMarketingVisitorId();
    if (!visitorId) return;
    try {
      await apiFetch(`${API_BASE_URL}/marketing/visits/associate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ anonymous_visitor_id: visitorId }),
      });
    } catch (error) {
      // Signup performs the primary association; this completes the
      // email-verification/MFA path without blocking authentication.
    }
  };

  const finishAuthentication = async () => {
    await associateMarketingVisitorAfterRegistration();
    const storedReauthReturnPath = sessionStorage.getItem(REAUTH_RETURN_PATH_KEY);
    const reauthReturnPath = storedReauthReturnPath === '/admin/account-management'
      ? storedReauthReturnPath
      : null;
    sessionStorage.removeItem(REAUTH_RETURN_PATH_KEY);
    setIsAuthenticated(true);
    navigate(location.state?.afterAuthPath || reauthReturnPath || '/dashboard', {
      replace: true,
      state: location.state?.afterAuthState,
    });
  };

  const handleMfaSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMfaLoading(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/auth/mfa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_token: challengeToken, code: mfaCode }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to verify the sign-in code');
      await finishAuthentication();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setMfaLoading(false);
    }
  };

  const resendMfaCode = async () => {
    setError('');
    setMfaLoading(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/auth/mfa/email-code/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_token: challengeToken }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to send a new sign-in code');
      setChallengeToken(data.challenge_token || challengeToken);
      setMfaCode('');
      setMfaMessage('A new sign-in code was sent to your verified email address. It expires in 15 minutes.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setMfaLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await apiFetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.detail === 'account_not_found') {
          setError('No account found for this email.');
          return;
        }
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : 'Please check the form and try again.'
        );
      }

      const data = await response.json();
      if (data.status === 'mfa_required') {
        setChallengeToken(data.challenge_token);
        setPassword('');
        setMfaCode('');
        setMfaMessage('We sent a six-digit sign-in code to your verified email address. It expires in 15 minutes.');
        setAuthStage('challenge');
        return;
      }
      await finishAuthentication();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const policyError = getPasswordPolicyError(password);
    if (policyError) {
      setPasswordPolicyError(policyError);
      setError(policyError);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const response = await apiFetch(`${API_BASE_URL}/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password,
          ...buildMarketingSignupField(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : 'Please check the form and try again.'
        );
      }

      const data = await response.json();
      if (data.status === "email_verification_required") {
        setPassword("");
        navigate("/verify-email", {
          replace: true,
          state: { email, marketingRegistration: Boolean(getMarketingVisitorId()) },
        });
        return;
      }
      throw new Error("Unable to start email verification");
    } catch (err) {
      setError(err.message);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError('');
    setPassword('');
    setConfirmPassword('');
    setPasswordPolicyError('');
  };

  if (authStage !== 'credentials') {
    return (
      <div className="public-page mfa-setup-page">
        <nav className="public-nav">
          <div className="public-nav-container">
            <Link to="/" className="public-nav-brand"><img src="/assets/black_peak_analytics_logo_transparent_240.png" alt="Black Peak Analytics" className="nav-brand-logo" /></Link>
          </div>
        </nav>
        <div className="login-section">
          <div className="login-container">
            <div className="login-card">
              <h1 className="login-title">Verify Your Sign-In</h1>
              <p className="login-subtitle">{mfaMessage || 'Enter the six-digit code sent to your verified email address.'}</p>
              {error && <div className="login-error" role="alert">{error}</div>}
              <form className="login-form" onSubmit={handleMfaSubmit}>
                <div className="login-form-group">
                  <label className="login-label" htmlFor="mfa-code">Email sign-in code</label>
                  <input id="mfa-code" className="login-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" required value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} />
                </div>
                <button className="login-button" type="submit" disabled={mfaLoading}>{mfaLoading ? 'Verifying…' : 'Verify and Sign In'}</button>
              </form>
              <button className="login-signup-link mfa-cancel-button" type="button" disabled={mfaLoading} onClick={resendMfaCode}>Send a new code</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-page">
      {/* Navigation - Premium Compact Header */}
      <nav className="public-nav">
        <div className="public-nav-container">
          <Link to="/" className="public-nav-brand">
            <img 
              src="/assets/black_peak_analytics_logo_transparent_240.png" 
              alt="Black Peak Analytics" 
              className="nav-brand-logo" 
            />
          </Link>
          <div className="public-nav-links">
            <Link to="/" className="public-nav-link">Home</Link>
            <Link to="/pricing" className="public-nav-link">Pricing</Link>
          </div>
        </div>
      </nav>

      {/* Auth Section */}
      <div className="login-section">
        <div className="login-container">
          <div className="login-card">
            {mode === 'login' ? (
              <>
                <h1 className="login-title">Welcome Back</h1>
                <p className="login-subtitle">
                  Sign in to access your investment research dashboard
                </p>

                {error && (
                  <div className="login-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleLoginSubmit} className="login-form">
                  <div className="login-form-group">
                    <label className="login-label" htmlFor="login-email">Email</label>
                    <input
                      id="login-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="login-input"
                    />
                  </div>

                  <div className="login-form-group">
                    <label className="login-label" htmlFor="login-password">Password</label>
                    <PasswordInput
                      id="login-password"
                      label="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                    />
                  </div>

                  <div className="login-password-help">
                    <Link className="login-signup-link" to="/forgot-password">Forgot password?</Link>
                  </div>

                  <button type="submit" className="login-button">
                    Sign In
                  </button>
                </form>

                <div className="login-footer">
                  <p className="login-footer-text">
                    Don't have an account?{' '}
                    <button 
                      onClick={switchMode}
                      className="login-signup-link"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    >
                      Sign up
                    </button>
                  </p>
                </div>
              </>
            ) : (
              <>
                <h1 className="login-title">Create Account</h1>
                <p className="login-subtitle">
                  Join Black Peak Analytics and start finding value beyond the price
                </p>

                {error && (
                  <div className="login-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSignupSubmit} className="login-form">
                  <div className="login-form-group">
                    <label className="login-label" htmlFor="signup-email">Email</label>
                    <input
                      id="signup-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="login-input"
                    />
                  </div>

                  <div className="login-form-group">
                    <label className="login-label" htmlFor="signup-password">Password</label>
                    <PasswordInput
                      id="signup-password"
                      label="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={password}
                      onChange={(event) => {
                        const nextPassword = event.target.value;
                        setPassword(nextPassword);
                        const nextPolicyError = getPasswordPolicyError(nextPassword);
                        setPasswordPolicyError(nextPolicyError);
                        if (!nextPolicyError) setError('');
                      }}
                      required
                    />
                    <p className="password-policy-hint">{PASSWORD_POLICY_MESSAGE}</p>
                    {passwordPolicyError && <p className="password-policy-error" role="alert">{passwordPolicyError}</p>}
                  </div>

                  <div className="login-form-group">
                    <label className="login-label" htmlFor="signup-confirm-password">Confirm Password</label>
                    <PasswordInput
                      id="signup-confirm-password"
                      label="confirm password"
                      autoComplete="new-password"
                      minLength={8}
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      required
                    />
                  </div>

                  <button type="submit" className="login-button">
                    Create Account
                  </button>
                </form>

                <div className="login-footer">
                  <p className="login-footer-text">
                    Already have an account?{' '}
                    <button 
                      onClick={switchMode}
                      className="login-signup-link"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
